/**
 * The steps between the team and signing (screens §P6 Next step), in the order the page asks for them. The sticky bar, the beats strip,
 * the Decide badge, the cockpit's "Waiting in Decide" and the rail checklist all read this one list; "Review & sign →" is the step when none is left.
 */
import type { TeamState } from "@bsim/contracts";
import { thesisMissing } from "@bsim/contracts";
import { callRequiredIn, type DecisionFamily } from "@bsim/domain";
import { deskLabeler, type DeskLabeler } from "./labels";
import { quotedTitle } from "../format/labels";

export type NextStep =
  | { readonly kind: "call"; readonly label: string }
  | { readonly kind: "rationale"; readonly label: string; readonly family: DecisionFamily }
  | { readonly kind: "event"; readonly label: string; readonly injectionId: string }
  | { readonly kind: "thesis"; readonly label: string }
  | { readonly kind: "review"; readonly label: string };
export type NextStepKind = NextStep["kind"];

/** Every unmet item, in order; a rationale step names its family by the catalog's desk label (`desk`). */
export function stepsToSign(team: TeamState, desk: DeskLabeler = deskLabeler(undefined)): readonly Exclude<NextStep, { kind: "review" }>[] {
  const draft = team.draft;
  const steps: Exclude<NextStep, { kind: "review" }>[] = [];
  if (callRequiredIn(team.round?.roundNo ?? 0) && team.forecast?.state !== "committed") steps.push({ kind: "call", label: "Your forecast" });
  for (const family of draft?.missingRationales ?? []) steps.push({ kind: "rationale", family, label: `Write the ${desk(family)} reason` });
  for (const e of team.pendingEvents) if (e.response === null && e.status === "delivered") steps.push({ kind: "event", injectionId: e.injectionId, label: `Answer the card: ${quotedTitle(e.card.title)}` });
  if (draft !== null && thesisMissing(draft.thesis)) steps.push({ kind: "thesis", label: "Add your thesis line" });
  return steps;
}

/** The last round of the run is tagged in the bar, and Review says what the score counts (D6): this year, then two more years the plan runs on, projected and not played. */
export const FINAL_ROUND_TAG = "Final round";
export const FINAL_ROUND_LINE = "This is the final round. Your bank is counted at the end of this year plus the two look-through years, which are projected, not played.";
/** True in the run's last round. */
export const isFinalRound = (team: TeamState): boolean => team.round !== null && team.round.roundNo >= team.cohort.roundCount;

/** The review beat's name: a solo run has no signature step, it submits. */
export const reviewLabel = (solo: boolean): string => (solo ? "Review & submit" : "Review & sign");

export function nextStep(team: TeamState): NextStep {
  return stepsToSign(team)[0] ?? { kind: "review", label: `${reviewLabel(team.cohort.mode === "solo")} →` };
}

/** How many steps stand between the team and signing. */
export const outstandingSteps = (team: TeamState): number => stepsToSign(team).length;

/** The line the cockpit shows for a step: the thesis says why it is asked (only when no decision family changed). */
const summaryOf = (s: NextStep): string => (s.kind === "thesis" ? "Thesis line (needed because no decision changed this round)" : s.label.replace(/^Write the /, "").replace(/^Answer the card: /, "Card: "));

/** "Your forecast · Growth rationale · Thesis line (needed because no decision changed this round)": the unmet steps in a line. */
export const stepSummary = (steps: readonly NextStep[]): string => steps.map(summaryOf).join(" \u00b7 ");

const lower = (text: string): string => text.charAt(0).toLowerCase() + text.slice(1);

/** The bar's words for one step left, in plain words: "x". */
export function barSentence(step: NextStep, desk: DeskLabeler): string {
  switch (step.kind) {
    case "call": return "make your forecast";
    case "rationale": return `finish the ${lower(desk(step.family))} reason`;
    case "event": return "answer the card";
    case "thesis": return "write your thesis line";
    case "review": return "review and submit";
  }
}

/** The bar's short name for a step in a list of several: "forecast, card, thesis". */
export function barNoun(step: NextStep, desk: DeskLabeler): string {
  switch (step.kind) {
    case "call": return "forecast";
    case "rationale": return `${lower(desk(step.family))} reason`;
    case "event": return "card";
    case "thesis": return "thesis";
    case "review": return "review";
  }
}

/** The bar's list of steps, a repeated kind counted once ("2 cards", not "card, card"). */
export function barNouns(steps: readonly NextStep[], desk: DeskLabeler): string {
  const counts = new Map<string, number>();
  for (const s of steps) { const n = barNoun(s, desk); counts.set(n, (counts.get(n) ?? 0) + 1); }
  return [...counts].map(([n, c]) => (c === 1 ? n : `${c} ${n}s`)).join(", ");
}

/** The bar's button for the next step: "Next: finish the reason", never an arrow; the last step is "Review and submit" (or sign). */
export function barButton(step: NextStep, solo: boolean): string {
  switch (step.kind) {
    case "call": return "Make your forecast";
    case "rationale": return "Next: finish the reason";
    case "event": return "Next: answer the card";
    case "thesis": return "Next: thesis line";
    case "review": return solo ? "Review and submit" : "Review and sign";
  }
}
